import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isVisualizacao } from '../utils/permissoes';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const brl = (n) => (Number(n) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBR = (d) => d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—';

// Mensagem motivacional pela quantidade de motos. Os cortes eram 15/11/6
// (meta fixa de 15); agora a meta é configurável, então viraram proporção
// — 11/15 = 73%, 6/15 = 40% — o que reproduz os cortes antigos quando meta=15.
function msgMotivacional(motos, faltam, meta) {
  const alvo = meta > 0 ? meta : 15;
  const pct = motos / alvo;
  if (motos >= alvo) return 'META ATINGIDA! Comissão desbloqueada ✅';
  if (pct >= 0.73) return `Quase lá! Faltam ${faltam} ${faltam === 1 ? 'moto' : 'motos'} para desbloquear sua comissão 🔥`;
  if (pct >= 0.4) return 'Boa fase! Você está no caminho certo 🚀';
  return 'Continue! Cada venda conta 💪';
}

// Barra de progresso de uma meta por categoria
function MetaBar({ m }) {
  const pct = m.meta > 0 ? Math.min(100, Math.round((m.vendido / m.meta) * 100)) : (m.vendido > 0 ? 100 : 0);
  const bateu = m.vendido >= m.meta && m.meta > 0;
  const cor = bateu ? '#2D7D4F' : '#1A6DFF';
  return (
    <div title={`${m.vendido} vendidas / ${m.meta} meta (${pct}%)`} style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#3C4655', marginBottom: '3px' }}>
        <span>{m.label}</span>
        <span style={{ fontWeight: 600, color: bateu ? '#2D7D4F' : '#5A6673' }}>{m.vendido} / {m.meta} <span style={{ color: '#9AA3AE' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: '8px', background: '#EEF1F5', borderRadius: '5px', overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: cor, borderRadius: '5px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// Card de um vendedor
function CardVendedor({ v, meta }) {
  const pct = Math.min(100, Math.round((v.total_motos / meta) * 100));
  const atingiu = v.meta_atingida;
  const corBarra = atingiu ? '#2D7D4F' : '#F39C12';
  // soma das metas de categoria == meta_total do mês
  const metaTotal = (v.metas || []).reduce((s, m) => s + m.meta, 0);

  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '0.5px solid rgba(26,58,92,0.12)', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#1C2533' }}>{v.nome}</div>
          <div style={{ fontSize: '12px', color: '#9AA3AE', marginTop: '2px' }}>
            {v.total_motos} {v.total_motos === 1 ? 'moto vendida' : 'motos vendidas'}
            {v.carros_excluidos > 0 && <span> · {v.carros_excluidos} carro(s) não contam</span>}
          </div>
        </div>
        <div style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap', color: atingiu ? '#2D7D4F' : '#6B7A8D', background: atingiu ? '#EAF5EE' : '#F4F6F9' }}>
          {atingiu ? 'META ATINGIDA ✓' : 'BLOQUEADA'}
        </div>
      </div>

      {/* Barra de progresso */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ height: '10px', background: '#EEF1F5', borderRadius: '5px', overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: corBarra, borderRadius: '5px', transition: 'width 0.4s ease' }} />
        </div>
        <div style={{ fontSize: '12px', marginTop: '6px', color: atingiu ? '#2D7D4F' : '#5A6673', fontWeight: atingiu ? 700 : 500 }}>
          {atingiu
            ? `META ATINGIDA ✓ — ${v.total_motos} / ${meta} motos`
            : `${v.total_motos} / ${meta} motos — faltam ${v.faltam} para desbloquear a comissão`}
        </div>
        {/* mensagem motivacional dinâmica */}
        <div style={{ fontSize: '12.5px', marginTop: '6px', fontWeight: 600, color: atingiu ? '#2D7D4F' : '#B45309' }}>
          {msgMotivacional(v.total_motos, v.faltam, meta)}
        </div>
      </div>

      {/* Metas por categoria (mês) */}
      {v.metas && v.metas.length > 0 && (
        <div style={{ marginBottom: '14px', borderTop: '1px solid #EEF1F5', paddingTop: '12px' }}>
          <div style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Metas por categoria</div>
          {v.metas.map((m) => <MetaBar key={m.key} m={m} />)}
        </div>
      )}

      {/* 1) POTENCIAL MÁXIMO (meta completa) — sempre visível, destaque azul */}
      <div style={{ background: '#1A2332', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', color: '#fff' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.5px', textTransform: 'uppercase', opacity: 0.75 }}>🎯 Potencial máximo (meta completa)</div>
        <div style={{ fontSize: '26px', fontWeight: 800, margin: '3px 0 5px' }}>{brl(v.potencial_maximo)}</div>
        <div style={{ fontSize: '12.5px', opacity: 0.85 }}>
          Se bater a meta de {metaTotal} motos, você pode ganhar até {brl(v.potencial_maximo)}
        </div>
        {v.potencial_maximo_detalhe && v.potencial_maximo_detalhe.some((d) => d.qtd > 0) && (
          <div style={{ marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '8px' }}>
            {v.potencial_maximo_detalhe.filter((d) => d.qtd > 0).map((d) => (
              <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '2px 0', opacity: 0.9 }}>
                <span>{d.qtd} {d.label} × R$ {d.unit}</span>
                <span style={{ fontWeight: 600 }}>{brl(d.subtotal)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 2) POTENCIAL ATUAL + 3) COMISSÃO A PAGAR */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '140px', background: '#F7F9FB', borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            💰 Potencial atual
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: atingiu ? '#9AA3AE' : '#EA580C', marginTop: '2px' }}>{brl(v.potencial)}</div>
          <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>Com as vendas atuais</div>
        </div>
        {/* Comissão real: só desbloqueada */}
        <div style={{ flex: 1, minWidth: '140px', background: atingiu ? '#ECFDF3' : '#F7F9FB', border: atingiu ? '1px solid #16A34A' : '1px solid transparent', borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', color: atingiu ? '#2D7D4F' : '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
            ✅ Comissão a pagar
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, color: atingiu ? '#2D7D4F' : '#B4BCC6', marginTop: '2px' }}>
            {atingiu ? brl(v.comissao_real) : brl(0)}
          </div>
          {!atingiu && <div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '2px' }}>{`🔒 bloqueada até ${meta} motos`}</div>}
        </div>
      </div>

      {/* Detalhamento por faixa */}
      {v.breakdown.length > 0 && (
        <div style={{ borderTop: '1px solid #EEF1F5', paddingTop: '12px', marginBottom: '10px' }}>
          {v.breakdown.map((b) => (
            <div key={b.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#3C4655', padding: '3px 0' }}>
              <span>{b.count} × {b.label} <span style={{ color: '#9AA3AE' }}>(R$ {b.unit})</span></span>
              <span style={{ fontWeight: 600 }}>{brl(b.subtotal)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Lista de vendas do mês */}
      {v.vendas.length > 0 && (
        <details style={{ marginTop: '6px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '12.5px', color: '#1A6DFF', fontWeight: 600, outline: 'none' }}>
            Ver {v.vendas.length} venda(s) do mês
          </summary>
          <div style={{ overflowX: 'auto', marginTop: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead>
                <tr>
                  <th style={th}>Data</th>
                  <th style={th}>Cliente</th>
                  <th style={th}>Moto</th>
                  <th style={th}>Tipo</th>
                  <th style={{ ...th, textAlign: 'right' }}>Comissão</th>
                </tr>
              </thead>
              <tbody>
                {v.vendas.map((s, i) => (
                  <tr key={i} style={{ color: '#3C4655' }}>
                    <td style={tdC}>{dataBR(s.data_venda)}</td>
                    <td style={tdC}>{s.cliente}</td>
                    <td style={tdC}>{s.moto}</td>
                    <td style={tdC}>{s.tipo}</td>
                    <td style={{ ...tdC, textAlign: 'right', fontWeight: 600 }}>{brl(s.valor_comissao)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ============================================================================
// Modal "Configurar regras" (somente admin).
//
// Grava em DOIS lugares, de propósito:
//  * meta de desbloqueio + R$ por categoria -> tabela `comissao_regras`
//    (era hardcoded em routes/comissao.js e motoClassifier.js);
//  * coluna "Meta" -> tabela `metas_vendas` do mês selecionado (o backend
//    converte o número absoluto em percentual). A meta JÁ era configurável
//    pelo Dashboard; duplicá-la em comissao_regras faria as duas telas
//    divergirem, então aqui só escrevemos no mesmo lugar que o Dashboard lê.
//
// A cilindrada (cc de/até) existe porque a faixa de uma moto é inferida do
// texto do modelo: sem faixa de cc, uma categoria nova nunca pegaria vendas.
// ============================================================================
const inp = { padding: '7px 9px', borderRadius: '7px', border: '1px solid rgba(26,58,92,0.18)', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", color: '#1C2533', width: '100%', boxSizing: 'border-box' };
const th = { padding: '10px 12px', background: '#F5F7FA', color: '#9AA3AE', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const tdC = { padding: '12px 12px', borderBottom: '0.5px solid rgba(26,58,92,0.07)' };
const btn = (bg, fg) => ({ padding: '9px 16px', borderRadius: '8px', border: 'none', background: bg, color: fg, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif" });

const METAS_CAT = [
  { key: 'eletricas', label: 'Elétricas' },
  { key: 'c125_190',  label: 'Convencionais ≤190cc' },
  { key: 'c200_plus', label: 'Acima de 200cc' },
];

function ModalRegras({ mes, ano, periodoLabel, onFechar, onSalvo }) {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [metaDesbloqueio, setMetaDesbloqueio] = useState(15);
  const [cats, setCats] = useState([]);
  const [metas, setMetas] = useState({ eletricas: 0, c125_190: 0, c200_plus: 0 });

  useEffect(() => {
    let vivo = true;
    api.get('/comissao/regras', { params: { mes, ano } })
      .then(({ data }) => {
        if (!vivo) return;
        setMetaDesbloqueio(data.meta_desbloqueio);
        setCats(data.categorias.map((c) => ({
          ...c,
          cc_min: c.cc_min === null || c.cc_min === undefined ? '' : c.cc_min,
          cc_max: c.cc_max === null || c.cc_max === undefined ? '' : c.cc_max,
        })));
        setMetas({ eletricas: data.metas.eletricas, c125_190: data.metas.c125_190, c200_plus: data.metas.c200_plus });
      })
      .catch((e) => vivo && setErro(e.response?.data?.error || 'Erro ao carregar regras'))
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [mes, ano]);

  const editar = (i, campo, valor) => setCats((cs) => cs.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));
  const marcarPadrao = (i) => setCats((cs) => cs.map((c, j) => ({ ...c, padrao: j === i })));
  const remover = (i) => setCats((cs) => cs.filter((_, j) => j !== i));
  const adicionar = () => setCats((cs) => [...cs, { key: '', nome: '', tipo: 'conv', cc_min: '', cc_max: '', comissao: 0, padrao: false }]);

  const num = (v) => Number(v) || 0;
  const metaTotal = METAS_CAT.reduce((s, m) => s + num(metas[m.key]), 0);
  const temEletrica = cats.some((c) => c.tipo === 'eletrica');

  const salvar = async () => {
    setErro('');
    if (!(Number(metaDesbloqueio) > 0)) return setErro('A meta de desbloqueio deve ser maior que zero.');
    if (cats.length === 0) return setErro('Informe ao menos uma categoria.');
    if (cats.some((c) => !String(c.nome || '').trim())) return setErro('Toda categoria precisa de um nome.');
    if (metaTotal <= 0) return setErro('A soma das metas por categoria deve ser maior que zero.');
    setSalvando(true);
    try {
      await api.put('/comissao/regras', {
        meta_desbloqueio: Number(metaDesbloqueio),
        categorias: cats.map((c) => ({
          key: c.key || undefined,
          nome: String(c.nome).trim(),
          tipo: c.tipo === 'eletrica' ? 'eletrica' : 'conv',
          cc_min: c.tipo === 'eletrica' || c.cc_min === '' ? null : Number(c.cc_min),
          cc_max: c.tipo === 'eletrica' || c.cc_max === '' ? null : Number(c.cc_max),
          comissao: num(c.comissao),
          padrao: !!c.padrao,
        })),
        metas: { mes, ano, eletricas: num(metas.eletricas), c125_190: num(metas.c125_190), c200_plus: num(metas.c200_plus) },
      });
      onSalvo();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar regras');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(15,25,40,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '100%', maxWidth: '760px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1C2533', margin: 0 }}>Configurar regras de comissão</h2>
            <p style={{ fontSize: '12.5px', color: '#9AA3AE', margin: '4px 0 0' }}>
              Valem para toda a loja. As metas por categoria são do período <strong>{periodoLabel}</strong>.
            </p>
          </div>
          <button onClick={onFechar} style={{ ...btn('transparent', '#9AA3AE'), fontSize: '20px', padding: '0 6px', lineHeight: 1 }}>×</button>
        </div>

        {carregando ? (
          <div style={{ color: '#9AA3AE', padding: '30px', textAlign: 'center' }}>Carregando...</div>
        ) : (
          <>
            {/* Meta de desbloqueio */}
            <div style={{ background: '#F7F9FB', borderRadius: '10px', padding: '14px 16px', marginBottom: '18px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#3C4655', display: 'block', marginBottom: '6px' }}>
                Meta para desbloquear a comissão
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input type="number" min="1" value={metaDesbloqueio}
                  onChange={(e) => setMetaDesbloqueio(e.target.value)} style={{ ...inp, width: '100px' }} />
                <span style={{ fontSize: '12.5px', color: '#9AA3AE' }}>
                  motos/mês por vendedor · abaixo disso a comissão fica bloqueada
                </span>
              </div>
            </div>

            {/* Categorias de comissão */}
            <div style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Valor da comissão por categoria
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, minWidth: '160px' }}>Categoria</th>
                    <th style={{ ...th, width: '110px' }}>Tipo</th>
                    <th style={{ ...th, width: '150px' }}>Cilindrada (cc)</th>
                    <th style={{ ...th, width: '110px' }}>Comissão (R$)</th>
                    <th style={{ ...th, width: '62px', textAlign: 'center' }} title="Recebe as motos cuja cilindrada não dá para ler no modelo">Padrão</th>
                    <th style={{ ...th, width: '40px' }} />
                  </tr>
                </thead>
                <tbody>
                  {cats.map((c, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #F0F3F7' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <input value={c.nome} placeholder="Nome da categoria"
                          onChange={(e) => editar(i, 'nome', e.target.value)} style={inp} />
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <select value={c.tipo} onChange={(e) => editar(i, 'tipo', e.target.value)}
                          disabled={temEletrica && c.tipo !== 'eletrica'}
                          title={temEletrica && c.tipo !== 'eletrica' ? 'Já existe uma categoria de elétricas' : ''}
                          style={{ ...inp, cursor: 'pointer' }}>
                          <option value="conv">Convencional</option>
                          <option value="eletrica">Elétrica</option>
                        </select>
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {c.tipo === 'eletrica' ? (
                          <span style={{ fontSize: '12px', color: '#9AA3AE' }}>pelo combustível</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <input type="number" min="0" placeholder="de" value={c.cc_min}
                              onChange={(e) => editar(i, 'cc_min', e.target.value)} style={{ ...inp, width: '62px' }} />
                            <span style={{ color: '#9AA3AE' }}>–</span>
                            <input type="number" min="0" placeholder="∞" value={c.cc_max}
                              onChange={(e) => editar(i, 'cc_max', e.target.value)} style={{ ...inp, width: '62px' }} />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <input type="number" min="0" step="1" value={c.comissao}
                          onChange={(e) => editar(i, 'comissao', e.target.value)} style={inp} />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <input type="radio" name="cat_padrao" checked={!!c.padrao} disabled={c.tipo === 'eletrica'}
                          onChange={() => marcarPadrao(i)} style={{ cursor: c.tipo === 'eletrica' ? 'not-allowed' : 'pointer' }} />
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <button onClick={() => remover(i)} title="Remover categoria"
                          style={{ ...btn('transparent', '#DC2626'), padding: '4px 8px', fontWeight: 700 }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={adicionar} style={{ ...btn('transparent', '#1A6DFF'), border: '1px solid rgba(26,58,92,0.2)', marginTop: '10px' }}>+ Adicionar categoria</button>
            <p style={{ fontSize: '11.5px', color: '#9AA3AE', margin: '8px 0 0' }}>
              A cilindrada é lida do texto do modelo. Deixe o campo <em>até</em> vazio para “sem teto”.
              A categoria marcada como <strong>Padrão</strong> recebe as motos sem cilindrada legível (ex.: “Phoenix”)
              e as que caírem numa lacuna entre faixas.
            </p>

            {/* Metas por categoria (gravadas em metas_vendas) */}
            <div style={{ borderTop: '1px solid #EEF1F5', margin: '18px 0 0', paddingTop: '16px' }}>
              <div style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                Metas de {periodoLabel} (por vendedor)
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {METAS_CAT.map((m) => (
                  <div key={m.key} style={{ flex: 1, minWidth: '150px' }}>
                    <label style={{ fontSize: '12px', color: '#5A6673', display: 'block', marginBottom: '4px' }}>{m.label}</label>
                    <input type="number" min="0" value={metas[m.key]}
                      onChange={(e) => setMetas((s) => ({ ...s, [m.key]: e.target.value }))} style={inp} />
                  </div>
                ))}
                <div style={{ minWidth: '110px' }}>
                  <label style={{ fontSize: '12px', color: '#5A6673', display: 'block', marginBottom: '4px' }}>Total</label>
                  <div style={{ ...inp, background: '#F7F9FB', fontWeight: 700, color: '#1C2533' }}>{metaTotal}</div>
                </div>
              </div>
              <p style={{ fontSize: '11.5px', color: '#9AA3AE', margin: '8px 0 0' }}>
                Mesma meta usada no Dashboard (é gravada lá). Motos de até 99cc contam em “Convencionais ≤190cc”.
              </p>
            </div>

            {erro && <div style={{ color: '#DC2626', background: '#FEF2F2', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginTop: '14px' }}>{erro}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button onClick={onFechar} style={{ ...btn('transparent', '#6B7A8D'), border: '1px solid rgba(26,58,92,0.2)' }}>Cancelar</button>
              <button onClick={salvar} disabled={salvando} style={{ ...btn('#1A6DFF', '#fff'), opacity: salvando ? 0.6 : 1 }}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Comissao() {
  const { usuario } = useAuth();
  const somenteLeitura = isVisualizacao(usuario);
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [showRegras, setShowRegras] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const { data } = await api.get('/comissao', { params: { mes, ano } });
      setDados(data);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao carregar comissões');
    } finally {
      setLoading(false);
    }
  }, [mes, ano]);

  useEffect(() => { carregar(); }, [carregar]);

  const anos = [];
  for (let a = hoje.getFullYear(); a >= hoje.getFullYear() - 3; a--) anos.push(a);

  const selectStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(26,58,92,0.15)', fontSize: '13px', fontFamily: "'DM Sans',system-ui,sans-serif", color: '#1C2533', background: '#fff', cursor: 'pointer' };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Título + seletor de período */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#1C2533', margin: 0 }}>Comissão por vendedor</h1>
          <p style={{ fontSize: '13px', color: '#9AA3AE', margin: '4px 0 0' }}>
            Meta: {dados?.meta ?? 15} motos/mês para desbloquear · apenas motos contam
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {dados?.is_admin && !somenteLeitura && (
            <button onClick={() => setShowRegras(true)}
              style={{ ...selectStyle, background: 'transparent', border: '1px solid rgba(26,58,92,0.2)', color: '#1A3A5C', fontWeight: 600 }}>
              ⚙ Configurar regras
            </button>
          )}
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={selectStyle}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={ano} onChange={(e) => setAno(Number(e.target.value))} style={selectStyle}>
            {anos.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {loading && <div style={{ color: '#9AA3AE', padding: '40px', textAlign: 'center' }}>Carregando...</div>}
      {erro && <div style={{ color: '#DC2626', background: '#FEF2F2', padding: '14px', borderRadius: '8px' }}>{erro}</div>}

      {!loading && !erro && dados && (
        <>
          {/* Regras vigentes — todos veem; só admin edita (botão acima) */}
          {dados.regras?.categorias?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '12.5px', color: '#5A6673' }}>
              <span style={{ fontSize: '11px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Regras aplicadas</span>
              {dados.regras.categorias.map((c) => (
                <span key={c.key} style={{ background: '#fff', border: '1px solid rgba(26,58,92,0.12)', borderRadius: '20px', padding: '4px 11px' }}>
                  {c.nome} <strong style={{ color: '#1C2533' }}>{brl(c.comissao)}</strong>
                </span>
              ))}
              {dados.regras.padrao && (
                <span style={{ color: '#B45309', fontSize: '11.5px' }} title="Nenhuma configuração salva: usando os valores padrão do sistema">
                  (padrão do sistema)
                </span>
              )}
            </div>
          )}

          {/* Consolidado (admin) */}
          {dados.is_admin && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px', background: '#1A2332', borderRadius: '12px', padding: '18px 20px', color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '12px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comissão total a pagar — {dados.periodo_label}</div>
                <div style={{ fontSize: '28px', fontWeight: 800, marginTop: '4px' }}>{brl(dados.consolidado.total_comissao_real)}</div>
              </div>
              <div style={{ flex: 1, minWidth: '160px', background: '#fff', borderRadius: '12px', padding: '18px 20px', border: '0.5px solid rgba(26,58,92,0.12)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '12px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Potencial total</div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: '#5A6673', marginTop: '4px' }}>{brl(dados.consolidado.total_potencial)}</div>
              </div>
              <div style={{ flex: 1, minWidth: '140px', background: '#fff', borderRadius: '12px', padding: '18px 20px', border: '0.5px solid rgba(26,58,92,0.12)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '12px', color: '#9AA3AE', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Motos vendidas</div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: '#1C2533', marginTop: '4px' }}>{dados.consolidado.total_motos}</div>
              </div>
            </div>
          )}

          {/* Cards */}
          {dados.vendedores.length === 0 ? (
            <div style={{ color: '#9AA3AE', padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              Nenhum vendedor encontrado para {dados.periodo_label}.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: dados.is_admin ? 'repeat(auto-fill, minmax(340px, 1fr))' : '1fr', gap: '16px' }}>
              {dados.vendedores.map((v) => <CardVendedor key={v.vendedor_id} v={v} meta={dados.meta} />)}
            </div>
          )}
        </>
      )}

      {showRegras && (
        <ModalRegras
          mes={mes}
          ano={ano}
          periodoLabel={dados?.periodo_label || `${MESES[mes - 1]} de ${ano}`}
          onFechar={() => setShowRegras(false)}
          onSalvo={() => { setShowRegras(false); carregar(); }}
        />
      )}
    </div>
  );
}
